import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { Bell, Camera, Check, Clock3, KeyRound, Lock, MapPin, Plus, ScrollText, ShieldCheck, Smartphone, Sparkles, Wifi } from "lucide-react";
import { BrandLogo } from "@/components/flow/BrandLogos";
import { BrandMark } from "@/components/flow/Brand";
import { brandStyle } from "@/lib/brandColor";
import { EASE } from "@/lib/motion";
import { t } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/* ---------------------------------------------------------------------------
   JourneyLoop — the whole product in ~24 seconds, on the owner's phone, with the
   story told alongside it.
   Seven states, one after the other: address → branded estimate → offer → sign on
   the phone → "Owner signed" → guided onboarding → ready for your PMS. The phone is the canvas; the
   manager's side appears as two small Listed notifications over its edge.

   Under the phone: seven segments (the active one fills over its duration), who
   acts (Owner / You), what happens, and one line on why it matters.

   Motion vocabulary matches the homepage phone (HeroStage): the tenant's app bar
   stays put while content pushes underneath (iOS), the success screen *reveals*
   instead of pushing, and the loop restart is a quiet fade — transform/opacity
   only. Pauses when scrolled away or the tab is hidden; with reduced motion it
   shows one still frame and the steps become a static, clickable list.
--------------------------------------------------------------------------- */

// The demo tenant is DigiHome itself — same lavender the site uses for its primary CTA (--lavender-cta).
// The homepage passes a visitor's brand instead (name, colour, domain, logo) and the whole phone re-brands.
const DEMO = { name: "DigiHome", color: "#D496FF", domain: "digihome.no", logoUrl: "/brand/digihome-mark.svg" };
const BrandCtx = createContext(DEMO);
const useBrand = () => useContext(BrandCtx);
const OWNER = "Anna Berg";
const ADDRESS = "Strandveien 12";
const ADDRESS_FULL = "Strandveien 12, 5003 Bergen";
const ADDRESS_ALT = "Strandveien 12, 1367 Snarøya";
const W = 292;
const H = 600;
const STATUS_H = 48;
const BAR_H = 40;
const HOME_H = 16;
/* Warm titanium: the same diagonal sheen as the homepage phone, in DigiHome's warm-ink family. */
const PHONE_BODY = "linear-gradient(152deg, #6A655D 0%, #3B3833 18%, #1E1C19 46%, #262320 72%, #4C4841 100%)";
const KEY_BG = "#34312B";
const BRAND = "var(--demo-brand)";
const BRAND_FG = "var(--demo-brand-fg)";
const BRAND_TEXT = "var(--demo-brand-text)";

/* `enter` is how the screen arrives: push (next page in the same flow), reveal (the success moment), reset (loop restart).
   Durations leave time to read: every screen finishes its own choreography ≥ 0.4 s before the button press,
   and the press comes ≈ 0.45 s before the next screen. */
export const LOOP_STEPS = [
  { id: "address", ms: 3400, clock: "09:01", enter: "reset", side: "owner" },
  { id: "estimate", ms: 3400, clock: "09:02", enter: "push", side: "owner" },
  { id: "offer", ms: 3000, clock: "09:16", enter: "push", side: "owner" },
  { id: "sign", ms: 3600, clock: "14:31", enter: "push", side: "owner" },
  { id: "signed", ms: 3200, clock: "14:32", enter: "reveal", side: "you" },
  { id: "onboard", ms: 3800, clock: "16:40", enter: "push", side: "owner" },
  { id: "ready", ms: 3200, clock: "17:02", enter: "push", side: "you" },
];
const STILL = 4; // reduced motion → the "Owner signed" frame

const IOS = [0.32, 0.72, 0, 1];
const OUT = [0.4, 0, 1, 1];
const screenVariants = {
  hidden: (m) => (m === "push" ? { opacity: 0, x: 60, y: 0, scale: 1 } : m === "reveal" ? { opacity: 0, x: 0, y: 0, scale: 0.98 } : { opacity: 0, x: 0, y: 14, scale: 1 }),
  show: (m) => ({ opacity: 1, x: 0, y: 0, scale: 1, transition: m === "push" ? { duration: 0.56, ease: IOS } : { duration: 0.5, ease: EASE, delay: 0.04 } }),
  exit: (m) =>
    m === "push"
      ? { opacity: 0, x: -44, transition: { duration: 0.34, ease: OUT } }
      : m === "reveal"
        ? { opacity: 0, scale: 1.01, transition: { duration: 0.28, ease: OUT } }
        : { opacity: 0, y: -10, transition: { duration: 0.3, ease: OUT } },
};

/* ---------- time ---------- */
function useHidden() {
  const [hidden, setHidden] = useState(() => typeof document !== "undefined" && document.visibilityState === "hidden");
  useEffect(() => {
    const f = () => setHidden(document.visibilityState === "hidden");
    document.addEventListener("visibilitychange", f);
    return () => document.removeEventListener("visibilitychange", f);
  }, []);
  return hidden;
}

/** Step sequencer that really pauses: remaining time is kept across pause/resume. */
function useSequencer(steps, paused, start = 0) {
  const [i, setI] = useState(start);
  const [cycle, setCycle] = useState(0);
  const remaining = useRef(steps[start].ms);
  const jumped = useRef(false); // a manual jump resets the budget; the cleanup must not subtract elapsed time then
  useEffect(() => {
    if (paused) return undefined;
    const began = performance.now();
    let fired = false;
    const id = setTimeout(() => {
      fired = true;
      const next = (i + 1) % steps.length;
      remaining.current = steps[next].ms;
      if (next === 0) setCycle((c) => c + 1);
      setI(next);
    }, remaining.current);
    return () => {
      clearTimeout(id);
      if (jumped.current) jumped.current = false;
      else if (!fired) remaining.current = Math.max(80, remaining.current - (performance.now() - began));
    };
  }, [i, cycle, paused, steps]);
  const jump = useCallback(
    (n) => {
      jumped.current = true;
      remaining.current = steps[n].ms;
      setCycle((c) => c + 1);
      setI(n);
    },
    [steps],
  );
  return { i, cycle, jump };
}

/** `true` after `ms` (immediately with reduced motion). */
function useAfter(ms, reduced) {
  const [on, setOn] = useState(!!reduced);
  useEffect(() => {
    if (reduced) return undefined;
    const id = setTimeout(() => setOn(true), ms);
    return () => clearTimeout(id);
  }, [ms, reduced]);
  return on;
}

function useTyped(text, { speed = 48, delay = 160, reduced }) {
  const [n, setN] = useState(reduced ? text.length : 0);
  useEffect(() => {
    if (reduced) return undefined;
    let id;
    let k = 0;
    const start = setTimeout(() => {
      id = setInterval(() => {
        k += 1;
        setN(k);
        if (k >= text.length) clearInterval(id);
      }, speed);
    }, delay);
    return () => {
      clearTimeout(start);
      clearInterval(id);
    };
  }, [text, speed, delay, reduced]);
  return text.slice(0, n);
}

function useCountUp(to, { delay = 150, duration = 800, reduced }) {
  const [v, setV] = useState(reduced ? to : 0);
  useEffect(() => {
    if (reduced) return undefined;
    let raf;
    let t0;
    let last = -1;
    const tick = (now) => {
      if (t0 == null) t0 = now;
      const p = Math.min(1, (now - t0) / duration);
      const e = 1 - Math.pow(1 - p, 3);
      // quantise to 25s so the screen re-renders a few dozen times, not every frame
      const v = p >= 1 ? to : Math.round((to * e) / 25) * 25;
      if (v !== last) {
        last = v;
        setV(v);
      }
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    const id = setTimeout(() => {
      raf = requestAnimationFrame(tick);
    }, delay);
    return () => {
      clearTimeout(id);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [to, delay, duration, reduced]);
  return v;
}

const fmt = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, "\u202f");

/** Column width + viewport → the phone's scale (and whether we are on a narrow screen). */
function useScale(ref) {
  const [s, setS] = useState({ scale: 0.7, compact: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const calc = () => {
      const w = el.clientWidth || 360;
      const vh = window.innerHeight || 800;
      const compact = window.innerWidth < 640;
      const byW = (w - (compact ? 56 : 72)) / W;
      const byH = (vh - 400) / H;
      setS({ scale: Math.max(0.56, Math.min(compact ? 0.76 : 0.84, byW, byH)), compact });
    };
    calc();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(calc) : null;
    ro?.observe(el);
    window.addEventListener("resize", calc);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", calc);
    };
  }, [ref]);
  return s;
}

/* ====================================================================== */
export function JourneyLoop({ brand: brandProp, className, testId = "ea-journey-loop" }) {
  const reduced = !!useReducedMotion();
  const root = useRef(null);
  const inView = useInView(root, { amount: 0.25 });
  const hidden = useHidden();
  const paused = reduced || !inView || hidden;
  const { i, cycle, jump } = useSequencer(LOOP_STEPS, paused, reduced ? STILL : 0);
  const { scale, compact } = useScale(root);
  const step = LOOP_STEPS[i];
  const hasBrand = !!brandProp;
  const brand = useMemo(
    () => ({ name: brandProp?.name || DEMO.name, color: brandProp?.color || DEMO.color, domain: brandProp?.domain || DEMO.domain, logoUrl: hasBrand ? brandProp?.logoUrl || "" : DEMO.logoUrl }),
    [brandProp?.name, brandProp?.color, brandProp?.domain, brandProp?.logoUrl, hasBrand],
  );
  // A new brand (a visitor's site just loaded) → the story starts over, in their colours.
  const sig = `${brand.name}|${brand.color}|${brand.domain}|${brand.logoUrl}`;
  const prevSig = useRef(sig);
  useEffect(() => {
    if (prevSig.current === sig) return;
    prevSig.current = sig;
    if (!reduced) jump(0);
  }, [sig, jump, reduced]);

  return (
    <BrandCtx.Provider value={brand}>
    <div ref={root} className={cn("relative w-full demo-brand-scope", className)} style={brandStyle(brand.color)} data-testid={testId} data-step={step.id} data-brand={brand.name} data-paused={paused ? "true" : "false"} data-reduced={reduced ? "true" : "false"} role="group" aria-label={t("ea.loop.aria")}>
      {/* No grain / backdrop blur on this panel: blend modes and frosted glass over a constantly moving phone repaint every frame. */}
      <div className="relative overflow-hidden rounded-[28px] sm:rounded-[36px] hero-stage-panel">
        <span aria-hidden className="pointer-events-none absolute inset-0 dot-grid-masked dot-grid-ink opacity-60" />
        {/* chips — whose screen, whose brand */}
        <div className="relative z-10 flex items-center justify-between gap-3 px-5 sm:px-6 pt-5">
          <span className="inline-flex max-w-full items-center gap-2 h-8 rounded-full border border-ink/[0.08] bg-white/80 pl-2.5 pr-3.5 text-[12.5px] font-medium text-ink/80" data-testid="ea-loop-surface">
            <Smartphone className="h-3.5 w-3.5 shrink-0 text-ink/45" strokeWidth={1.9} />
            <span className="truncate">{t("ea.loop.owner_chip")}</span>
          </span>
          <span className="inline-flex items-center gap-1.5 text-[11.5px] text-ink/50" data-testid="ea-loop-brand-chip">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: BRAND }} aria-hidden />
            {t("ea.loop.brand_chip")}
          </span>
        </div>

        <div className="relative z-10 px-5 sm:px-6 pt-5 pb-6 sm:pb-7">
          <div className="relative mx-auto" style={{ width: Math.round(W * scale), height: Math.round(H * scale) }}>
            {/* halo: a soft radial gradient does the feathering itself — no blur filter to re-rasterise while it breathes */}
            <span aria-hidden className="phone-halo pointer-events-none absolute rounded-full" style={{ left: -90, right: -90, top: 30, bottom: 10, background: "radial-gradient(closest-side, var(--demo-brand-glow) 0%, var(--demo-brand-soft) 42%, transparent 72%)" }} />
            <div className="absolute left-0 top-0 will-change-transform" style={{ width: W, height: H, transform: `scale(${scale})`, transformOrigin: "top left" }}>
              <Phone step={step} cycle={cycle} reduced={reduced} />
              <Notifications step={step} cycle={cycle} reduced={reduced} compact={compact} />
            </div>
          </div>
          <Transport index={i} cycle={cycle} paused={paused} reduced={reduced} onJump={jump} />
        </div>
      </div>
    </div>
    </BrandCtx.Provider>
  );
}

/* ---------- progress: the active step fills over its duration (transform only, pausable) ---------- */
function Fill({ n, cycle, ms, paused, reduced, className }) {
  return (
    <span key={`${n}-${cycle}`} className={cn("absolute inset-0 origin-left rounded-full bg-ink", className)} style={reduced ? { transform: "scaleX(1)" } : { animation: `eaFill ${ms}ms linear forwards`, animationPlayState: paused ? "paused" : "running" }} />
  );
}

/* ---------- transport: six segments + who acts, what happens, and why it matters (one line) ---------- */
function Transport({ index, cycle, paused, reduced, onJump }) {
  const s = LOOP_STEPS[index];
  return (
    <div className="mt-5 sm:mt-6" data-testid="ea-loop-transport">
      <div className="flex gap-1.5" role="tablist" aria-label={t("ea.loop.aria")}>
        {LOOP_STEPS.map((st, n) => {
          const done = n < index;
          const active = n === index;
          return (
            <button key={st.id} type="button" role="tab" aria-selected={active} aria-label={t(`ea.loop.step.${st.id}`)} onClick={() => onJump(n)} className="group relative block h-7 flex-1 min-w-0 focus-visible:outline-none" data-testid={`ea-loop-seg-${st.id}`} data-active={active ? "true" : "false"} data-done={done ? "true" : "false"}>
              <span className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 overflow-hidden rounded-full bg-ink/[0.10] transition-colors duration-200 group-hover:bg-ink/[0.18] group-focus-visible:bg-ink/[0.22]">
                {active ? <Fill n={n} cycle={cycle} ms={st.ms} paused={paused} reduced={reduced} /> : <span className="absolute inset-0 origin-left rounded-full bg-ink transition-transform duration-300 ease-out" style={{ transform: done ? "scaleX(1)" : "scaleX(0)" }} />}
              </span>
            </button>
          );
        })}
      </div>
      {/* the new caption rises in while the old one lifts out — no empty gap between steps */}
      <div className="mt-2 relative min-h-[84px] sm:min-h-[64px]" aria-live="polite">
        <AnimatePresence initial={false}>
          <motion.div key={index} initial={reduced ? false : { opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={reduced ? undefined : { opacity: 0, y: -8 }} transition={{ duration: 0.34, ease: EASE }} className="absolute inset-x-0 top-0">
            <div className="flex items-start gap-2.5 min-w-0">
              <span className="mt-px inline-flex h-[20px] shrink-0 items-center rounded-full px-2 text-[10.5px] font-medium uppercase tracking-[0.12em]" style={s.side === "owner" ? { background: "var(--demo-brand-soft)", color: BRAND_TEXT } : { background: "rgba(21,19,15,0.08)", color: "var(--ink)" }} data-testid="ea-loop-side">{t(`ea.loop.side.${s.side}`)}</span>
              <p className="min-w-0 text-[14.5px] font-medium tracking-[-0.01em] text-ink leading-[1.35] sm:truncate" data-testid="ea-loop-caption">{t(`ea.loop.step.${s.id}`)}</p>
            </div>
            <p className="mt-1 text-[13px] leading-[1.45] text-ink/60 line-clamp-2" data-testid="ea-loop-caption-desc">{t(`ea.loop.desc.${s.id}`)}</p>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ---------- the phone ---------- */
function Phone({ step, cycle, reduced }) {
  const Screen = SCREENS[step.id];
  const mode = step.enter;
  return (
    <div className="relative h-full w-full select-none" data-testid="ea-phone" data-screen={step.id}>
      <span aria-hidden className="pointer-events-none absolute left-[10%] right-[10%] -bottom-6 h-10 rounded-[50%] bg-ink/35" style={{ filter: "blur(22px)" }} />
      <div className="relative h-full w-full rounded-[48px] p-[7px] shadow-[0_50px_100px_-40px_rgba(21,19,15,0.55),0_24px_48px_-28px_rgba(21,19,15,0.4)]" style={{ background: PHONE_BODY }}>
        <span aria-hidden className="pointer-events-none absolute inset-0 rounded-[48px] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.3),inset_0_0_0_2.5px_rgba(0,0,0,0.35)]" />
        <span aria-hidden className="pointer-events-none absolute inset-[2px] rounded-[46px] shadow-[inset_0_1px_0_rgba(255,255,255,0.14),inset_0_-1px_0_rgba(0,0,0,0.5)]" />
        <span aria-hidden className="absolute -left-[3px] top-[104px] h-[24px] w-[3px] rounded-l-[2px] shadow-[inset_1px_0_0_rgba(255,255,255,0.2)]" style={{ background: KEY_BG }} />
        <span aria-hidden className="absolute -left-[3px] top-[148px] h-[52px] w-[3px] rounded-l-[2px] shadow-[inset_1px_0_0_rgba(255,255,255,0.2)]" style={{ background: KEY_BG }} />
        <span aria-hidden className="absolute -left-[3px] top-[210px] h-[52px] w-[3px] rounded-l-[2px] shadow-[inset_1px_0_0_rgba(255,255,255,0.2)]" style={{ background: KEY_BG }} />
        <span aria-hidden className="absolute -right-[3px] top-[170px] h-[84px] w-[3px] rounded-r-[2px] shadow-[inset_-1px_0_0_rgba(255,255,255,0.2)]" style={{ background: KEY_BG }} />
        <div className="relative h-full w-full rounded-[41px] bg-[#070605] p-[3px]">
          <div className="relative h-full w-full rounded-[38px] bg-canvas overflow-hidden" style={{ transform: "translateZ(0)", clipPath: "inset(0 round 38px)", WebkitClipPath: "inset(0 round 38px)", contain: "paint", isolation: "isolate" }} data-testid="ea-phone-screen">
            {/* the tenant's app bar stays put; only the page content moves */}
            <div className="absolute inset-x-0 z-30" style={{ top: STATUS_H, height: BAR_H }}>
              <TenantBar />
            </div>
            <div className="absolute inset-x-0" style={{ top: STATUS_H + BAR_H, bottom: HOME_H }}>
              <AnimatePresence mode="popLayout" initial={false} custom={mode}>
                <motion.div key={`${step.id}-${cycle}`} custom={mode} variants={screenVariants} initial={reduced ? "show" : "hidden"} animate="show" exit="exit" className="absolute inset-0 flex flex-col bg-canvas will-change-transform" data-testid={`ea-screen-${step.id}`} data-enter={mode}>
                  <Screen reduced={reduced} />
                </motion.div>
              </AnimatePresence>
            </div>
            {/* status bar + island */}
            <div className="absolute inset-x-0 top-0 z-40 flex items-end justify-between px-6 pb-[8px] text-[12px] font-semibold text-ink bg-canvas" style={{ height: STATUS_H }} aria-hidden>
              <span className="tabular-nums tracking-[-0.01em]">{step.clock}</span>
              <span className="absolute left-1/2 top-[10px] -translate-x-1/2 h-[24px] w-[86px] rounded-full bg-[#080706] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.05)]">
                <span className="absolute right-[8px] top-1/2 -translate-y-1/2 h-[8px] w-[8px] rounded-full bg-[#0f1a24] ring-1 ring-white/10"><span className="absolute left-[2px] top-[1.5px] h-[2.5px] w-[2.5px] rounded-full bg-[#2a4f6a] opacity-90" /></span>
              </span>
              <span className="inline-flex items-center gap-[5px]">
                <span className="flex items-end gap-[1.5px]">{[4, 6, 8, 10].map((h) => <span key={h} className="w-[3px] rounded-[1px] bg-current" style={{ height: h }} />)}</span>
                <Wifi className="h-[12px] w-[12px]" strokeWidth={2.4} />
                <span className="relative ml-px h-[10px] w-[20px] rounded-[3px] border-[1.5px] border-current opacity-90"><span className="absolute inset-[1.5px] right-[4px] rounded-[1px] bg-current" /></span>
              </span>
            </div>
            <div className="absolute inset-x-0 bottom-0 z-40 flex items-center justify-center pointer-events-none bg-canvas" style={{ height: HOME_H }} aria-hidden><span className="h-[4px] w-[96px] rounded-full bg-ink/25" /></div>
            <span aria-hidden className="pointer-events-none absolute inset-0 z-20 rounded-[38px]" style={{ background: "linear-gradient(118deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.05) 24%, rgba(255,255,255,0) 42%)" }} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- shared screen furniture (tenant-branded) ---------- */
function TenantBar() {
  const brand = useBrand();
  return (
    <div className="h-full flex items-center gap-2 px-4 border-b border-hairline bg-canvas" data-testid="ea-tenant-bar">
      {brand.logoUrl ? (
        <img src={brand.logoUrl} alt="" className="h-[22px] w-[22px] shrink-0 rounded-[7px] object-contain" />
      ) : (
        <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[7px] font-heading font-bold text-[11px]" style={{ background: BRAND, color: BRAND_FG }}>{brand.name.charAt(0)}</span>
      )}
      <span className="font-heading font-bold text-[13px] tracking-[-0.02em] text-ink truncate">{brand.name}</span>
      <span className="ml-auto inline-flex max-w-[45%] items-center gap-1 h-6 rounded-full bg-fill pl-2 pr-2.5 text-[10px] text-ink/55"><Lock className="h-2.5 w-2.5 shrink-0" strokeWidth={2.2} /><span className="truncate">{brand.domain}</span></span>
    </div>
  );
}

function BrandButton({ children, pressed, className, reduced }) {
  return (
    <motion.span animate={pressed && !reduced ? { scale: [1, 0.965, 1] } : { scale: 1 }} transition={{ duration: 0.42, ease: EASE }} className={cn("relative h-10 rounded-full flex items-center justify-center gap-2 text-[13px] font-medium overflow-hidden", className)} style={{ background: BRAND, color: BRAND_FG }} data-pressed={pressed ? "true" : "false"}>
      {children}
      {pressed && !reduced && <motion.span aria-hidden initial={{ opacity: 0.35, scale: 0.4 }} animate={{ opacity: 0, scale: 2.2 }} transition={{ duration: 0.7, ease: "easeOut" }} className="pointer-events-none absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />}
    </motion.span>
  );
}

const Eyebrow = ({ children }) => <p className="text-[10.5px] font-medium uppercase tracking-[0.14em]" style={{ color: BRAND_TEXT }}>{children}</p>;

const Appear = ({ when, children, className, y = 6, reduced }) => (
  <motion.div initial={reduced ? false : { opacity: 0, y }} animate={when ? { opacity: 1, y: 0 } : { opacity: 0, y }} transition={{ duration: 0.42, ease: EASE }} className={className}>
    {children}
  </motion.div>
);

const Tick = ({ on, size = 16, className, mount = false }) => (
  <motion.span initial={mount ? { scale: 0.6, opacity: 0 } : false} animate={on ? { scale: 1, opacity: 1 } : { scale: 0.6, opacity: 0 }} transition={{ type: "spring", stiffness: 420, damping: 24 }} className={cn("flex shrink-0 items-center justify-center rounded-full", className)} style={{ height: size, width: size, background: BRAND, color: BRAND_FG }}>
    <Check style={{ height: Math.round(size * 0.56), width: Math.round(size * 0.56) }} strokeWidth={3.2} />
  </motion.span>
);

/* ---------- 1 · address ---------- */
const TRUST = [
  { k: 1, icon: ShieldCheck },
  { k: 2, icon: Clock3 },
  { k: 3, icon: Lock },
];
function ScreenAddress({ reduced }) {
  const brand = useBrand();
  const typed = useTyped(ADDRESS, { speed: 52, delay: 500, reduced }); // finishes ≈ 1.3 s
  const tap = useAfter(1650, reduced); // the owner taps the first suggestion
  const chosen = useAfter(1820, reduced); // list collapses, field shows the verified address
  const pressed = useAfter(2950, reduced);
  const listOpen = !reduced && typed.length >= 6 && !chosen;
  const typing = !reduced && typed.length < ADDRESS.length;
  return (
    <>
      <div className="flex-1 px-5 pt-5">
        <Eyebrow>{t("ea.loop.s1.eyebrow")}</Eyebrow>
        <p className="mt-2 font-heading font-bold text-[22px] leading-[1.1] tracking-[-0.03em] text-ink">{t("ea.loop.s1.title")}</p>
        <p className="mt-1.5 text-[12px] text-ink/55">{t("ea.loop.s1.sub")}</p>
        <p className="mt-6 text-[11px] font-medium text-ink/60 mb-1.5">{t("ea.loop.s1.label")}</p>
        <div className="relative">
          <div className="relative h-11 rounded-[12px] border bg-surface px-3 flex items-center gap-2 text-[13.5px] text-ink shadow-[0_0_0_4px_rgba(21,19,15,0.04)] transition-colors duration-300" style={{ borderColor: chosen ? BRAND : "rgba(21,19,15,0.18)" }}>
            <MapPin className="h-4 w-4 shrink-0 text-ink/40" strokeWidth={1.9} />
            <span className="truncate">
              {chosen ? ADDRESS_FULL : typed}
              {typing && <span className="caret-blink inline-block w-px h-[15px] align-middle bg-ink ml-px" />}
            </span>
          </div>
          {/* autocomplete — sits over whatever is below, like a real dropdown */}
          <AnimatePresence>
            {listOpen && (
              <motion.div initial={{ opacity: 0, y: -4, scale: 0.99 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -4, scale: 0.99, transition: { duration: 0.18 } }} transition={{ duration: 0.28, ease: EASE }} className="absolute inset-x-0 top-[calc(100%+6px)] z-10 origin-top rounded-[12px] border border-hairline bg-surface p-1 shadow-[0_18px_40px_-22px_rgba(21,19,15,0.35),0_2px_6px_rgba(21,19,15,0.05)]" data-testid="ea-address-suggestions">
                {[ADDRESS_FULL, ADDRESS_ALT].map((s, n) => (
                  <div key={s} className={cn("flex items-center gap-2 rounded-[9px] px-2 h-9 text-[12px] text-ink/80 transition-colors duration-150", n === 0 && tap && "bg-fill")}>
                    <MapPin className="h-3.5 w-3.5 shrink-0 text-ink/40" strokeWidth={1.9} />
                    <span className="truncate">{s}</span>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <Appear when={chosen} reduced={reduced} className="mt-2.5 flex items-center gap-2 text-[12px] text-ink/70">
          <Tick on size={16} />
          <span className="truncate">{t("ea.loop.s1.verified_full")}</span>
          <span className="ml-auto text-[10.5px] text-ink/45 shrink-0">{t("ea.loop.s1.verified")}</span>
        </Appear>
        <ul className="mt-6 space-y-2.5">
          {TRUST.map(({ k, icon: Icon }) => (
            <li key={k} className="flex items-center gap-2.5 text-[12px] text-ink/65">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[8px] bg-fill text-ink/70"><Icon className="h-3.5 w-3.5" strokeWidth={1.9} /></span>
              {t(`ea.loop.s1.trust${k}`, { brand: brand.name })}
            </li>
          ))}
        </ul>
      </div>
      <div className="px-5 pb-4"><BrandButton pressed={pressed} reduced={reduced}>{t("ea.loop.s1.cta")}</BrandButton></div>
    </>
  );
}

/* ---------- 2 · estimate ---------- */
const SEASON = [0.38, 0.42, 0.55, 0.7, 0.86, 1, 1, 0.98, 0.84, 0.62, 0.44, 0.5];
const MONTHS = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];
function ScreenEstimate({ reduced }) {
  const total = useCountUp(31400, { delay: 350, duration: 1100, reduced }); // lands ≈ 1.45 s
  const compare = useAfter(1650, reduced);
  const pressed = useAfter(2950, reduced);
  return (
    <>
      <div className="flex-1 px-5 pt-5">
        <Eyebrow>{t("ea.loop.s2.eyebrow")}</Eyebrow>
        <p className="mt-1.5 text-[12px] text-ink/55 truncate">{ADDRESS_FULL}</p>
        <p className="mt-3 font-heading font-bold text-[34px] leading-none tracking-[-0.04em] text-ink tabular-nums">€ {fmt(total)}</p>
        <p className="mt-1 text-[12px] text-ink/55">{t("ea.loop.s2.per_year")}</p>
        <div className="mt-4 grid grid-cols-2 gap-2">
          {[[t("ea.loop.s2.nightly"), "€ 165"], [t("ea.loop.s2.occ"), "62 %"]].map(([k, v]) => (
            <div key={k} className="rounded-[12px] border border-hairline bg-surface px-3 py-2.5">
              <p className="text-[10.5px] text-ink/50">{k}</p>
              <p className="mt-0.5 font-heading font-bold text-[16px] tracking-[-0.02em] text-ink tabular-nums">{v}</p>
            </div>
          ))}
        </div>
        {/* seasonality — peak months in the brand, the rest a wash of the same hue */}
        <div className="mt-4" aria-hidden>
          <div className="flex items-end gap-[5px] h-[54px]">
            {SEASON.map((v, k) => (
              <motion.span key={k} initial={reduced ? false : { scaleY: 0.08 }} animate={{ scaleY: v }} transition={{ delay: 0.4 + k * 0.05, duration: 0.65, ease: EASE }} className="flex-1 origin-bottom rounded-[3px]" style={{ height: 54, background: BRAND, opacity: k >= 4 && k <= 7 ? 1 : 0.38 }} />
            ))}
          </div>
          <div className="mt-1.5 flex gap-[5px]">
            {MONTHS.map((m, k) => <span key={k} className="flex-1 text-center text-[8.5px] font-medium text-ink/40 tabular-nums">{m}</span>)}
          </div>
        </div>
        <Appear when={compare} reduced={reduced} className="mt-3.5">
          <div className="flex items-center justify-between rounded-[12px] bg-fill/80 px-3 py-2.5">
            <div className="min-w-0">
              <p className="text-[10.5px] text-ink/50">{t("ea.loop.s2.vs")}</p>
              <p className="mt-0.5 text-[12.5px] font-medium text-ink/70 tabular-nums">{t("ea.loop.s2.vs_v")}</p>
            </div>
            <span className="inline-flex items-center gap-1 h-6 rounded-full px-2.5 text-[11px] font-semibold tabular-nums" style={{ background: BRAND, color: BRAND_FG }}><Sparkles className="h-3 w-3" strokeWidth={2.2} />{t("ea.loop.s2.vs_delta")}</span>
          </div>
        </Appear>
      </div>
      <div className="px-5 pb-4"><BrandButton pressed={pressed} reduced={reduced}>{t("ea.loop.s2.cta")}</BrandButton></div>
    </>
  );
}

/* ---------- 3 · offer ---------- */
function ScreenOffer({ reduced }) {
  const picked = useAfter(900, reduced);
  const net = useAfter(1400, reduced);
  const pressed = useAfter(2550, reduced);
  const opts = [[t("ea.loop.s3.opt1"), t("ea.loop.s3.opt1_v"), true], [t("ea.loop.s3.opt2"), t("ea.loop.s3.opt2_v"), false]];
  return (
    <>
      <div className="flex-1 px-5 pt-5">
        <Eyebrow>{t("ea.loop.s3.eyebrow")}</Eyebrow>
        <p className="mt-2 font-heading font-bold text-[20px] leading-[1.1] tracking-[-0.03em] text-ink">{t("ea.loop.s3.title")}</p>
        <div className="mt-4 grid grid-cols-2 gap-2">
          {opts.map(([k, v, first]) => {
            const on = first && picked;
            return (
              <motion.div key={k} animate={on && !reduced ? { scale: [1, 0.965, 1] } : { scale: 1 }} transition={{ duration: 0.36, ease: EASE }} className={cn("rounded-[12px] border bg-surface px-3 py-2.5 transition-[border-color,box-shadow] duration-300", !on && "border-hairline")} style={on ? { borderColor: BRAND, boxShadow: `0 0 0 1px ${BRAND}` } : undefined}>
                <div className="flex items-center justify-between gap-1">
                  <p className="text-[12px] font-medium text-ink">{k}</p>
                  <span className="relative flex h-[14px] w-[14px] items-center justify-center rounded-full border transition-colors duration-300" style={on ? { borderColor: BRAND } : { borderColor: "rgba(21,19,15,0.2)" }}>
                    <Tick on={on} size={14} className="absolute inset-0" />
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-ink/55">{v}</p>
              </motion.div>
            );
          })}
        </div>
        <ul className="mt-4 space-y-2">
          {[1, 2, 3, 4].map((n) => (
            <motion.li key={n} initial={reduced ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 + n * 0.08, duration: 0.4, ease: EASE }} className="flex items-center gap-2 text-[12.5px] text-ink/80">
              <span className="flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-full bg-fill text-ink"><Check className="h-[8px] w-[8px]" strokeWidth={3} /></span>
              {t(`ea.loop.s3.inc${n}`)}
            </motion.li>
          ))}
        </ul>
        <Appear when={net} reduced={reduced} className="mt-4">
          <div className="rounded-[12px] px-3 py-2.5 flex items-center justify-between gap-3" style={{ background: "var(--demo-brand-soft)" }}>
            <p className="text-[11px] leading-snug text-ink/65">{t("ea.loop.s3.net")}</p>
            <p className="font-heading font-bold text-[16px] tracking-[-0.02em] text-ink tabular-nums whitespace-nowrap">{t("ea.loop.s3.net_v")}</p>
          </div>
        </Appear>
      </div>
      <div className="px-5 pb-4"><BrandButton pressed={pressed} reduced={reduced}>{t("ea.loop.s3.cta")}</BrandButton></div>
    </>
  );
}

/* ---------- 4 · sign ---------- */
function ScreenSign({ reduced }) {
  const consent = useAfter(500, reduced);
  const signedBy = useAfter(2200, reduced);
  const pressed = useAfter(3150, reduced);
  const rows = [[t("ea.loop.s4.r1"), t("ea.loop.s4.r1v")], [t("ea.loop.s4.r2"), t("ea.loop.s4.r2v")], [t("ea.loop.s4.r3"), t("ea.loop.s4.r3v")], [t("ea.loop.s4.r4"), t("ea.loop.s4.r4v")]];
  return (
    <>
      <div className="flex-1 px-5 pt-5">
        <Eyebrow>{t("ea.loop.s4.eyebrow")}</Eyebrow>
        <p className="mt-2 font-heading font-bold text-[20px] leading-[1.1] tracking-[-0.03em] text-ink">{t("ea.loop.s4.title")}</p>
        <div className="mt-3 rounded-[12px] border border-hairline bg-surface divide-y divide-hairline">
          {rows.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between px-3 h-[30px] text-[12px]"><span className="text-ink/55">{k}</span><span className="font-medium text-ink">{v}</span></div>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2 text-[11.5px] text-ink/65">
          <span className="relative flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-[5px] border transition-colors duration-300" style={{ borderColor: consent ? BRAND : "rgba(21,19,15,0.25)", background: consent ? BRAND : "transparent" }}>
            <motion.span initial={false} animate={consent ? { scale: 1, opacity: 1 } : { scale: 0.5, opacity: 0 }} transition={{ type: "spring", stiffness: 420, damping: 24 }} style={{ color: BRAND_FG }} className="flex"><Check className="h-[10px] w-[10px]" strokeWidth={3.2} /></motion.span>
          </span>
          {t("ea.loop.s4.consent")}
        </div>
        <div className="mt-3 relative h-[92px] rounded-[12px] border border-dashed bg-surface overflow-hidden transition-colors duration-500" style={{ borderColor: signedBy ? BRAND : "rgba(21,19,15,0.22)" }} data-testid="ea-signature-pad">
          <span className="absolute left-3 top-2 text-[10px] text-ink/40">{t("ea.loop.s4.sign_here")}</span>
          <span aria-hidden className="absolute left-3 right-3 bottom-4 h-px bg-ink/15" />
          <svg viewBox="0 0 220 70" className="absolute inset-x-3 bottom-2 h-[64px] w-[calc(100%-24px)]" aria-hidden>
            <motion.path d="M6 46 C 18 12, 30 60, 44 34 S 66 8, 80 40 S 104 66, 118 30 S 142 12, 154 38 S 178 58, 190 32 S 206 26, 214 34" fill="none" stroke="var(--ink)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" initial={reduced ? { pathLength: 1 } : { pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.8, duration: 1.3, ease: [0.4, 0, 0.2, 1] }} />
          </svg>
        </div>
        <Appear when={signedBy} reduced={reduced} y={4} className="mt-2 flex items-center justify-between text-[10.5px] text-ink/50">
          <span>{t("ea.loop.s4.signed_by", { owner: OWNER })}</span>
          <span className="tabular-nums">14:31</span>
        </Appear>
      </div>
      <div className="px-5 pb-4"><BrandButton pressed={pressed} reduced={reduced}>{t("ea.loop.s4.cta")}</BrandButton></div>
    </>
  );
}

/* ---------- 5 · signed ---------- */
const NEXT = [
  { k: 1, icon: Camera },
  { k: 2, icon: KeyRound },
];
function ScreenSigned({ reduced }) {
  const brand = useBrand();
  const text = useAfter(500, reduced);
  const doc = useAfter(800, reduced);
  const next = useAfter(1100, reduced);
  const pressed = useAfter(2750, reduced);
  return (
    <>
      <div className="flex-1 px-6 pt-8 flex flex-col items-center text-center">
        <motion.span initial={reduced ? false : { scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.22, type: "spring", stiffness: 300, damping: 22 }} className="flex h-16 w-16 items-center justify-center rounded-full" style={{ background: BRAND, color: BRAND_FG }} data-testid="ea-signed-check">
          <Check className="h-7 w-7" strokeWidth={2.8} />
        </motion.span>
        <Appear when={text} reduced={reduced} className="mt-5">
          <p className="font-heading font-bold text-[24px] leading-none tracking-[-0.03em] text-ink">{t("ea.loop.s5.title")}</p>
          <p className="mt-2 text-[12.5px] text-ink/60 leading-[1.5] max-w-[24ch] mx-auto">{t("ea.loop.s5.sub", { brand: brand.name })}</p>
        </Appear>
        <Appear when={doc} reduced={reduced} className="mt-5 w-full">
          <div className="rounded-[12px] border border-hairline bg-surface px-3 py-2.5 text-left flex items-center gap-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-fill text-ink"><ScrollText className="h-4 w-4" strokeWidth={1.8} /></span>
            <div className="min-w-0">
              <p className="text-[12px] font-medium text-ink truncate">{t("ea.loop.s5.doc")}</p>
              <p className="text-[10.5px] text-ink/50 truncate">{ADDRESS} · {t("ea.loop.s5.doc_sub")}</p>
            </div>
          </div>
        </Appear>
        <Appear when={next} reduced={reduced} className="mt-4 w-full text-left">
          <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-ink/45">{t("ea.loop.s5.next_label")}</p>
          <ul className="mt-2 space-y-2">
            {NEXT.map(({ k, icon: Icon }) => (
              <li key={k} className="flex items-center gap-2.5 text-[12px] text-ink/75">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[8px] bg-fill text-ink/70"><Icon className="h-3.5 w-3.5" strokeWidth={1.9} /></span>
                {t(`ea.loop.s5.next${k}`)}
              </li>
            ))}
          </ul>
        </Appear>
      </div>
      <div className="px-5 pb-4"><BrandButton pressed={pressed} reduced={reduced}>{t("ea.loop.s5.next")}</BrandButton></div>
    </>
  );
}

/* ---------- 6 · onboarding (the owner, guided) ---------- */
const PHOTOS = [
  { src: "/img/interior-living.webp", at: 500 },
  { src: "/img/interior-kitchen.webp", at: 800 },
  { src: "/img/interior-bedroom.webp", at: 1100 },
];
function Toggle({ on, reduced }) {
  return (
    <span className="relative inline-flex h-[20px] w-[34px] shrink-0 items-center rounded-full transition-colors duration-300" style={{ background: on ? BRAND : "rgba(21,19,15,0.14)" }} aria-hidden>
      <motion.span initial={false} animate={{ x: on ? 16 : 2 }} transition={reduced ? { duration: 0 } : { type: "spring", stiffness: 500, damping: 30 }} className="absolute h-[16px] w-[16px] rounded-full bg-white shadow-[0_1px_2px_rgba(21,19,15,0.25)]" />
    </span>
  );
}
function ScreenOnboard({ reduced }) {
  const brand = useBrand();
  const p1 = useAfter(PHOTOS[0].at, reduced);
  const p2 = useAfter(PHOTOS[1].at, reduced);
  const p3 = useAfter(PHOTOS[2].at, reduced);
  const keys = useAfter(1750, reduced);
  const rule1 = useAfter(2250, reduced);
  const rule2 = useAfter(2600, reduced);
  const pressed = useAfter(3350, reduced);
  const shots = [p1, p2, p3];
  const done = (p3 ? 1 : 0) + (keys ? 1 : 0) + (rule2 ? 1 : 0);
  const Label = ({ children, ok }) => (
    <div className="flex items-center justify-between">
      <p className="text-[11px] font-medium text-ink/60">{children}</p>
      <Tick on={ok} size={14} />
    </div>
  );
  return (
    <>
      <div className="flex-1 px-5 pt-5">
        <Eyebrow>{t("ea.loop.s6b.eyebrow")}</Eyebrow>
        <p className="mt-2 font-heading font-bold text-[20px] leading-[1.1] tracking-[-0.03em] text-ink">{t("ea.loop.s6b.title")}</p>
        {/* three thin segments — one per part of the checklist */}
        <div className="mt-3 flex gap-1" aria-hidden>
          {[0, 1, 2].map((k) => (
            <span key={k} className="h-[3px] flex-1 overflow-hidden rounded-full bg-ink/[0.08]">
              <motion.span initial={false} animate={{ scaleX: k < done ? 1 : 0 }} transition={{ duration: 0.45, ease: EASE }} className="block h-full w-full origin-left rounded-full" style={{ background: BRAND }} />
            </span>
          ))}
        </div>
        <div className="mt-4">
          <Label ok={p3}>{t("ea.loop.s6b.photos")}</Label>
          <div className="mt-2 grid grid-cols-4 gap-1.5">
            {PHOTOS.map((ph, n) => (
              <motion.span key={ph.src} initial={false} animate={shots[n] ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }} transition={{ duration: 0.4, ease: EASE }} className="block aspect-square overflow-hidden rounded-[9px] bg-fill">
                <img src={ph.src} alt="" className="h-full w-full object-cover" loading="eager" decoding="async" />
              </motion.span>
            ))}
            <span className="flex aspect-square items-center justify-center rounded-[9px] border border-dashed text-ink/40" style={{ borderColor: "rgba(21,19,15,0.2)" }}><Plus className="h-3.5 w-3.5" strokeWidth={2} /></span>
          </div>
        </div>
        <div className="mt-4">
          <Label ok={keys}>{t("ea.loop.s6b.keys")}</Label>
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            {[t("ea.loop.s6b.keys1"), t("ea.loop.s6b.keys2")].map((k, n) => {
              const on = n === 0 && keys;
              return (
                <span key={k} className={cn("flex h-9 items-center justify-between rounded-[10px] border bg-surface px-2.5 text-[11.5px] font-medium text-ink transition-[border-color,box-shadow] duration-300", !on && "border-hairline")} style={on ? { borderColor: BRAND, boxShadow: `0 0 0 1px ${BRAND}` } : undefined}>
                  <span className="flex items-center gap-1.5 min-w-0"><KeyRound className="h-3.5 w-3.5 shrink-0 text-ink/50" strokeWidth={1.9} /><span className="truncate">{k}</span></span>
                  {on && <Tick on mount size={14} />}
                </span>
              );
            })}
          </div>
        </div>
        <div className="mt-4">
          <Label ok={rule2}>{t("ea.loop.s6b.rules")}</Label>
          <div className="mt-2 rounded-[12px] border border-hairline bg-surface divide-y divide-hairline">
            {[[t("ea.loop.s6b.rule1"), rule1], [t("ea.loop.s6b.rule2"), rule2]].map(([k, on]) => (
              <div key={k} className="flex items-center justify-between px-3 h-[34px] text-[12px] text-ink"><span>{k}</span><Toggle on={on} reduced={reduced} /></div>
            ))}
          </div>
        </div>
      </div>
      <div className="px-5 pb-4"><BrandButton pressed={pressed} reduced={reduced}>{t("ea.loop.s6b.cta", { brand: brand.name })}</BrandButton></div>
    </>
  );
}

/* ---------- 7 · ready ---------- */
const ITEMS = [
  { k: 1, icon: Camera, at: 350 },
  { k: 2, icon: KeyRound, at: 700 },
  { k: 3, icon: ScrollText, at: 1050 },
];
function ScreenReady({ reduced }) {
  const brand = useBrand();
  const t1 = useAfter(ITEMS[0].at, reduced);
  const t2 = useAfter(ITEMS[1].at, reduced);
  const t3 = useAfter(ITEMS[2].at, reduced);
  const pressed = useAfter(2750, reduced);
  const ticks = [t1, t2, t3];
  const done = ticks.filter(Boolean).length;
  const all = done === 3;
  return (
    <>
      <div className="flex-1 px-5 pt-5">
        <Eyebrow>{t("ea.loop.s6.eyebrow")}</Eyebrow>
        <p className="mt-2 font-heading font-bold text-[20px] leading-[1.1] tracking-[-0.03em] text-ink">{t("ea.loop.s6.title")}</p>
        <ul className="mt-4 space-y-2">
          {ITEMS.map(({ k, icon: Icon }, n) => (
            <li key={k} className="flex items-center gap-2.5 rounded-[12px] border border-hairline bg-surface px-3 h-11">
              <span className="flex h-7 w-7 items-center justify-center rounded-[8px] bg-fill text-ink"><Icon className="h-3.5 w-3.5" strokeWidth={1.8} /></span>
              <span className="text-[12.5px] font-medium text-ink flex-1">{t(`ea.loop.s6.i${k}`)}</span>
              <Tick on={ticks[n]} size={18} />
            </li>
          ))}
        </ul>
        {/* handover to the manager — the bar fills as each item lands */}
        <div className="mt-4 rounded-[12px] px-3 py-3" style={{ background: "var(--demo-brand-soft)" }}>
          <div className="flex items-center justify-between gap-3 text-[11.5px]">
            <span className="font-medium text-ink/80 truncate">{t("ea.loop.s6.summary", { brand: brand.name })}</span>
            <span className="text-ink/55 tabular-nums shrink-0">{t("ea.loop.s6.summary_sub", { n: done })}</span>
          </div>
          <span className="mt-2 block h-[4px] overflow-hidden rounded-full bg-ink/[0.08]">
            <motion.span initial={false} animate={{ scaleX: done / 3 }} transition={{ duration: 0.5, ease: EASE }} className="block h-full w-full origin-left rounded-full" style={{ background: BRAND }} />
          </span>
        </div>
        <Appear when={all} reduced={reduced} className="mt-3 flex items-center gap-2 text-[12px] text-ink/60">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-success" />{t("ea.loop.s6.done", { brand: brand.name })}
        </Appear>
      </div>
      <div className="px-5 pb-4"><BrandButton pressed={pressed} reduced={reduced}>{t("ea.loop.s6.cta")}</BrandButton></div>
    </>
  );
}

const SCREENS = { address: ScreenAddress, estimate: ScreenEstimate, offer: ScreenOffer, sign: ScreenSign, signed: ScreenSigned, onboard: ScreenOnboard, ready: ScreenReady };

/* ---------- the manager's side: two Listed notifications over the phone's top-right edge ---------- */
function Notifications({ step, cycle, reduced, compact }) {
  const show = step.id === "signed" || step.id === "ready";
  return (
    <AnimatePresence>
      {show && (
        <motion.div key={`${step.id}-${cycle}`} initial={reduced ? false : { opacity: 0, x: 18, y: -8, scale: 0.98 }} animate={{ opacity: 1, x: 0, y: 0, scale: 1 }} exit={reduced ? undefined : { opacity: 0, x: 12, transition: { duration: 0.22, ease: OUT } }} transition={{ delay: reduced ? 0 : step.id === "signed" ? 0.65 : 0.8, duration: 0.55, ease: EASE }} className="absolute z-20 top-[8px] w-[208px] rounded-[16px] border border-ink/[0.08] bg-surface p-3 shadow-[0_24px_60px_-24px_rgba(21,19,15,0.45),0_2px_6px_rgba(21,19,15,0.06)] will-change-transform" style={{ right: compact ? -78 : -112 }} data-testid={`ea-notification-${step.id}`}>
          <div className="flex items-center gap-2">
            <BrandMark className="h-[18px] w-[18px] rounded-[5px]" />
            <span className="text-[11px] font-medium text-ink/70">{t("ea.loop.card.app")}</span>
            <span className="ml-auto inline-flex items-center gap-1 text-[10.5px] text-ink/45"><Bell className="h-3 w-3" strokeWidth={2} />{t("ea.loop.card.now")}</span>
          </div>
          {step.id === "signed" ? (
            <div className="mt-2 flex items-start gap-2.5">
              <span className="mt-px flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success text-white"><Check className="h-3.5 w-3.5" strokeWidth={3} /></span>
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-ink leading-tight">{t("ea.loop.card.signed")}</p>
                <p className="mt-0.5 text-[11.5px] text-ink/55 truncate">{t("ea.loop.card.signed_sub", { address: ADDRESS, owner: OWNER })}</p>
              </div>
            </div>
          ) : (
            <div className="mt-2">
              <p className="text-[13px] font-semibold text-ink leading-tight">{t("ea.loop.card.ready")}</p>
              <p className="mt-0.5 text-[11.5px] text-ink/55">{t("ea.loop.card.ready_sub")}</p>
              <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-ink/60" data-testid="ea-notification-pms">
                {["guesty", "hostaway", "lodgify", "smoobu"].map((id) => <BrandLogo key={id} id={id} scale={0.6} />)}
                <span className="text-[10.5px] font-medium text-ink/50">{t("ea.loop.card.any_pms")}</span>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
